import { DailyAttendanceFilters, Equipment, EquipmentActivity } from "../types";

export const baseUrl = process.env.REACT_APP_API_BASE_URL || "/api/";

// Placeholder API service. Replace URLs with your real endpoints.
export async function fetchDailyAttendances(filters: DailyAttendanceFilters) {
  const params = new URLSearchParams();
  params.append("start_date", filters.startDate);
  params.append("end_date", filters.endDate);
  if (filters.portId) params.append("port_id", filters.portId);
  if (filters.shiftId) params.append("shift_id", filters.shiftId);
  if (filters.designationId)
    params.append("designation_id", filters.designationId);
  if (filters.groupByDate)
    params.append("group_by_date", String(filters.groupByDate));
  return fetch(`${baseUrl}attendance-report/?${params.toString()}`).then(
    (res) => res.json(),
  );
}

export async function fetchRegularAttendances(filters: DailyAttendanceFilters) {
  const params = new URLSearchParams();
  params.append("start_date", filters.startDate);
  params.append("end_date", filters.endDate);
  if (filters.portId) params.append("port_id", filters.portId);
  if (filters.shiftId) params.append("shift_id", filters.shiftId);
  if (filters.designationId)
    params.append("designation_id", filters.designationId);
  if (filters.groupByDate)
    params.append("group_by_date", String(filters.groupByDate));
  return fetch(`${baseUrl}regular-attendance-count/?${params.toString()}`).then(
    (res) => res.json(),
  );
}

export async function fetchPortWiseAttendances(portId: string, date: string) {
  // return fetch(`${baseUrl}/api/attendances/port?portId=${portId}&date=${date}`).then(res => res.json());
  return [];
}

export async function fetchShiftWiseAttendances(
  shiftId: string,
  date: string,
  portId?: string,
) {
  // return fetch(`/api/attendances/shift?shiftId=${shiftId}&date=${date}&portId=${portId || ''}`).then(res => res.json());
  return [];
}

export async function fetchPorts() {
  const res = await fetch(`${baseUrl}port/`);
  if (!res.ok) throw new Error("Failed to fetch ports");
  const data = await res.json();
  return { data: Array.isArray(data.data) ? data : [] };
}

export async function fetchShifts(portId: string) {
  const res = await fetch(`${baseUrl}shift/?port_id=${portId}`);
  if (!res.ok) throw new Error("Failed to fetch shifts");
  const data = await res.json();
  return { data: Array.isArray(data) ? data : [] };
}

export async function fetchDesignations() {
  const res = await fetch(`${baseUrl}designation/`);
  if (!res.ok) throw new Error("Failed to fetch designations");
  const data = await res.json();
  return { data: Array.isArray(data) ? data : [] };
}

export async function fetchEmployees() {
  const res = await fetch(`${baseUrl}employee/`);
  if (!res.ok) throw new Error("Failed to fetch employees");
  return res.json();
}

export async function fetchTotalCheckinsToday(): Promise<number> {
  try {
    const res = await fetch(`${baseUrl}today-attendance/`);
    if (res.ok) {
      const data = await res.json();
      return data.total_check_ins ?? 0;
    } else {
      throw new Error("Failed to load today's attendance count");
    }
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";
    throw new Error(`Error occurred: ${errorMessage}`);
  }
}

export async function fetchLocationTracking(
  employeeId: string,
  startDate: string,
  endDate: string,
) {
  const params = new URLSearchParams();
  params.append("employee_id", employeeId);
  params.append("start_date", startDate);
  params.append("end_date", endDate);
  const res = await fetch(`${baseUrl}locationtracking/?${params.toString()}`);
  if (!res.ok) throw new Error("Failed to fetch location tracking data");
  return res.json();
}

export async function login(employee_code: string, password: string) {
  const params = new URLSearchParams({
    employee_code: employee_code,
    password: password,
  });
  const res = await fetch(`${baseUrl}login/?${params.toString()}`);
  if (!res.ok) throw new Error("Invalid credentials");
  return res.json();
}

export async function fetchPortsList() {
  const res = await fetch(`${baseUrl}port/`);
  if (!res.ok) throw new Error("Failed to fetch ports");
  return res.json();
}

export async function createPort(port: { name: string; location: string }) {
  const res = await fetch(`${baseUrl}port/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(port),
  });
  if (!res.ok) throw new Error("Failed to create port");
  return res.json();
}

export async function updatePort(
  id: number,
  port: { name: string; location: string },
) {
  const res = await fetch(`${baseUrl}port/?id=${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(port),
  });
  if (!res.ok) throw new Error("Failed to update port");
  return res.json();
}

export async function deletePort(id: number) {
  const res = await fetch(`${baseUrl}port/?id=${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete port");
  return true;
}

export async function fetchPortById(id: number) {
  const res = await fetch(`${baseUrl}port/${id}/`);
  if (!res.ok) throw new Error("Failed to fetch port");
  return res.json();
}

export async function fetchDesignationsList() {
  const res = await fetch(`${baseUrl}designation/`);
  if (!res.ok) throw new Error("Failed to fetch designations");
  const data = await res.json();
  return { data: Array.isArray(data) ? data : [] };
}

export async function createDesignation(designation: {
  name: string;
  user_type: string;
  remote_checkin: boolean;
}) {
  const res = await fetch(`${baseUrl}designation/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(designation),
  });
  if (!res.ok) throw new Error("Failed to create designation");
  return res.json();
}

export async function updateDesignation(
  id: number,
  data: { name: string; user_type: string; remote_checkin: boolean },
) {
  const res = await fetch(`${baseUrl}designation/?id=${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update designation");
  return res.json();
}

export async function deleteDesignation(id: number) {
  const res = await fetch(`${baseUrl}designation/?id=${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete designation");
  return true;
}

export async function fetchTerminalsList() {
  const res = await fetch(`${baseUrl}site/`);
  if (!res.ok) throw new Error("Failed to fetch terminals");
  const data = await res.json();
  return { data: Array.isArray(data) ? data : [] };
}

export async function createTerminal(site: {
  name: string;
  latitude: number;
  longitude: number;
  geofence_area: number;
  port: number;
}) {
  const res = await fetch(`${baseUrl}site/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(site),
  });
  if (!res.ok) throw new Error("Failed to create site");
  return res.json();
}

export async function updateTerminal(
  id: number,
  data: {
    name: string;
    latitude: number;
    longitude: number;
    geofence_area: number;
    port: number;
  },
) {
  const res = await fetch(`${baseUrl}site/?id=${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update site");
  return res.json();
}

export async function deleteTerminal(id: number) {
  const res = await fetch(`${baseUrl}site/?id=${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete site");
  return true;
}

export async function fetchShiftsList() {
  const res = await fetch(`${baseUrl}shift/`);
  if (!res.ok) throw new Error("Failed to fetch shifts");
  const data = await res.json();
  return { data: Array.isArray(data) ? data : [] };
}

export async function createShift(shift: {
  name: string;
  start_time: string;
  end_time: string;
  duration_hours: number;
  port: number;
}) {
  const res = await fetch(`${baseUrl}shift/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(shift),
  });
  if (!res.ok) throw new Error("Failed to create shift");
  return res.json();
}

export async function updateShift(
  id: number,
  data: {
    name: string;
    start_time: string;
    end_time: string;
    duration_hours: number;
    port: number;
  },
) {
  const res = await fetch(`${baseUrl}shift/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update shift");
  return res.json();
}

export async function deleteShift(id: number) {
  const res = await fetch(`${baseUrl}shift/${id}/`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete shift");
  return true;
}

export async function fetchEmployeesList() {
  const res = await fetch(`${baseUrl}employee/`);
  if (!res.ok) throw new Error("Failed to fetch employees");
  const data = await res.json();
  return { data: Array.isArray(data.data) ? data.data : [] };
}

export async function createEmployee(employee: {
  profile_image?: string;
  employee_code: string;
  name: string;
  mobile_number: string;
  gender: string;
  password: string;
  date_of_birth: string;
  date_of_joining: string;
  port: number;
  designation: number;
}) {
  const res = await fetch(`${baseUrl}employee/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(employee),
  });
  if (!res.ok) throw new Error("Failed to create employee");
  return res.json();
}

export async function updateEmployee(
  id: number,
  data: {
    profile_image?: string;
    employee_code: string;
    name: string;
    mobile_number: string;
    gender: string;
    password: string;
    date_of_birth: string;
    date_of_joining: string;
    port: number;
    designation: number;
    is_active?: boolean;
  },
) {
  const res = await fetch(`${baseUrl}employee/?id=${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update employee");
  return res.json();
}

export async function deleteEmployee(id: number, employee?: any) {
  // Instead of deleting, mark employee as inactive
  let employeeData = employee;

  // If employee data is not provided, fetch it
  if (!employeeData) {
    const employeesResponse = await fetchEmployeesList();
    const employees = Array.isArray(employeesResponse.data)
      ? employeesResponse.data
      : [];
    employeeData = employees.find((e: any) => e.id === id);

    if (!employeeData) {
      throw new Error("Employee not found");
    }
  }

  // Update employee with is_active set to false
  const updateData = {
    profile_image: employeeData.profile_image || "",
    employee_code: employeeData.employee_code,
    name: employeeData.name,
    mobile_number: employeeData.mobile_number,
    gender: employeeData.gender,
    password: employeeData.password,
    date_of_birth: employeeData.date_of_birth,
    date_of_joining: employeeData.date_of_joining,
    port: employeeData.port,
    designation: employeeData.designation?.id || employeeData.designation,
    is_active: false,
  };

  const res = await fetch(`${baseUrl}employee/?id=${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updateData),
  });
  if (!res.ok) throw new Error("Failed to deactivate employee");
  return res.json();
}

export async function fetchEquipments(): Promise<Equipment[]> {
  const res = await fetch(`${baseUrl}equipment/`);
  if (!res.ok) throw new Error("Failed to fetch equipment");
  return res.json();
}

export async function createEquipment(equipment: {
  name: string;
}): Promise<Equipment> {
  const res = await fetch(`${baseUrl}equipment/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(equipment),
  });
  if (!res.ok) throw new Error("Failed to create equipment");
  return res.json();
}

export async function updateEquipment(
  id: number,
  equipment: { name: string },
): Promise<Equipment> {
  const res = await fetch(`${baseUrl}equipment/${id}/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(equipment),
  });
  if (!res.ok) throw new Error("Failed to update equipment");
  return res.json();
}

export async function deleteEquipment(id: number): Promise<void> {
  const res = await fetch(`${baseUrl}equipment/${id}/`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete equipment");
}

// Equipment Activities API
export async function fetchEquipmentActivities(
  equipmentId: number,
): Promise<EquipmentActivity[]> {
  const res = await fetch(
    `${baseUrl}equipment-activity/?equipment_id=${equipmentId}`,
  );
  if (!res.ok) throw new Error("Failed to fetch equipment activities");
  return res.json();
}

export async function createEquipmentActivity(
  activity: EquipmentActivity,
): Promise<EquipmentActivity> {
  const res = await fetch(`${baseUrl}equipment-activity/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(activity),
  });
  if (!res.ok) throw new Error("Failed to create equipment activity");
  return res.json();
}

export async function updateEquipmentActivity(
  activity: EquipmentActivity,
): Promise<EquipmentActivity> {
  if (!activity.id) throw new Error("Activity ID is required for update");
  const res = await fetch(`${baseUrl}equipment-activity/?id=${activity.id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(activity),
  });
  if (!res.ok) throw new Error("Failed to update equipment activity");
  return res.json();
}

export async function fetchEmployeeCount(): Promise<number> {
  try {
    const res = await fetch(`${baseUrl}employee-count/`);
    if (res.ok) {
      const data = await res.json();
      return data.total_employees ?? 0;
    } else {
      throw new Error(`Failed to get employee count: ${res.statusText}`);
    }
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";
    throw new Error(`Error occurred: ${errorMessage}`);
  }
}

export async function fetchPortWiseAttendanceToday() {
  try {
    const res = await fetch(`${baseUrl}port-wise-attendance/`);
    if (!res.ok) throw new Error("Failed to fetch port-wise attendance");
    const data = await res.json();
    return data;
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";
    throw new Error(`Error occurred: ${errorMessage}`);
  }
}

export async function fetchPortWiseAttendanceByDate(date: string) {
  try {
    const params = new URLSearchParams();
    params.append("date", date);
    const res = await fetch(
      `${baseUrl}port-wise-attendance/?${params.toString()}`,
    );
    if (!res.ok) throw new Error("Failed to fetch port-wise attendance");
    const data = await res.json();
    return data;
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";
    throw new Error(`Error occurred: ${errorMessage}`);
  }
}

export const fetchSiteTypes = async () => {
  const res = await fetch(`${baseUrl}site-types/`);
  if (!res.ok) throw new Error("Failed to fetch site types");
  const data = await res.json();
  return data;
};

export const createSiteType = async (newType: string) => {
  const res = await fetch(`${baseUrl}site-types/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type_name: newType }),
  });
  if (!res.ok) throw new Error("Failed to create site type");
  return res.json();
};

export const deleteSiteType = async (id?: number) => {
  if (!id) throw new Error("Site type ID is required");
  await fetch(`${baseUrl}site-types/${id}`, {
    method: "DELETE",
  });
};

export const fetchSites = async () => {
  const res = await fetch(`${baseUrl}pmsite/`);
  if (!res.ok) throw new Error("Failed to fetch sites");
  const data = await res.json();
  return data;
};

export const createSite = async (site: {
  site_name: string;
  site_type: number;
  location: string;
}) => {
  const res = await fetch(`${baseUrl}pmsite/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(site),
  });
  if (!res.ok) throw new Error("Failed to create site");
  return res.json();
};

export const updateSite = async (
  id: number,
  site: {
    site_name: string;
    site_type: number;
    location: string;
  },
) => {
  const res = await fetch(`${baseUrl}pmsite/?id=${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(site),
  });
  if (!res.ok) throw new Error("Failed to update site");
  return res.json();
};

export const deleteSite = async (id?: number) => {
  if (!id) throw new Error("Site ID is required");
  await fetch(`${baseUrl}pmsite/?id=${id}`, {
    method: "DELETE",
  });
};

// PMSitePlace API functions
export const fetchSitePlaces = async () => {
  const res = await fetch(`${baseUrl}pmsiteplace/`);
  if (!res.ok) throw new Error("Failed to fetch site places");
  const data = await res.json();
  return data;
};

export const createSitePlace = async (sitePlace: {
  site: number;
  place_name: string;
  reader_macid: string;
  rfid_antena: string;
}) => {
  const res = await fetch(`${baseUrl}pmsiteplace/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(sitePlace),
  });
  if (!res.ok) throw new Error("Failed to create site place");
  return res.json();
};

export const updateSitePlace = async (
  id: number,
  sitePlace: {
    site: number;
    place_name: string;
    reader_macid: string;
    rfid_antena: string;
  },
) => {
  const res = await fetch(`${baseUrl}pmsiteplace/?id=${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(sitePlace),
  });
  if (!res.ok) throw new Error("Failed to update site place");
  return res.json();
};

export const deleteSitePlace = async (id?: number) => {
  if (!id) throw new Error("Site place ID is required");
  await fetch(`${baseUrl}pmsiteplace/?id=${id}`, {
    method: "DELETE",
  });
};

export const fetchPreventiveMaintenanceReport = async (
  startDate: string,
  endDate: string,
  portId: string | null,
) => {
  const portParam = portId ? `&site_name=${portId}` : "";
  const res = await fetch(
    `${baseUrl}preventive_maintenance/filter/?from_date=${startDate}&to_date=${endDate}&${portParam}`,
  );
  if (!res.ok) throw new Error("Failed to fetch preventive maintenance report");
  return res.json();
};

export const fetchPreventiveMaintenanceStatus = async (
  startDate: string,
  endDate: string,
) => {
  const res = await fetch(
    `${baseUrl}preventive_maintenance_status/filter/?from_date=${startDate}&to_date=${endDate}`,
  );
  if (!res.ok) throw new Error("Failed to fetch preventive maintenance status");
  return res.json();
};

export const getPMCountForMonth = async () => {
  const res = await fetch(`${baseUrl}preventive-maintenance/completed-count/`);
  if (!res.ok) throw new Error("Failed to fetch PM count for month");
  const data = await res.json();
  return data.count ?? 0;
};

export const getEmployeeTargets = async (filter: any) => {
  const res = await fetch(
    `${baseUrl}employeetarget/?${new URLSearchParams(
      filter as any,
    ).toString()}`,
  );
  if (!res.ok) throw new Error("Failed to fetch employee targets");
  return res.json();
};

export const createEmployeeTarget = async (target: {
  employee_id: number;
  target: number | null;
}) => {
  const res = await fetch(`${baseUrl}employeetarget/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(target),
  });
  if (!res.ok) throw new Error("Failed to create employee target");
  return res.json();
};

export const updateEmployeeTarget = async (
  id: number,
  target: { employee: number; target: number | null },
) => {
  const res = await fetch(`${baseUrl}employeetarget/${id}/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(target),
  });
  if (!res.ok) throw new Error("Failed to update employee target");
  return res.json();
};

export const deleteEmployeeTarget = async (id: number) => {
  const res = await fetch(`${baseUrl}employeetarget/${id}/`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete employee target");
};

export const fetchPortAvailableCount = async () => {
  const res = await fetch(`${baseUrl}portavailablecount/`);
  if (!res.ok) throw new Error("Failed to fetch port available count");
  return res.json();
};

export const savePortAvailableCount = async (data: {
  port_id: number;
  available_count: number;
}) => {
  const res = await fetch(`${baseUrl}portavailablecount/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to save port available count");
  return res.json();
};
